import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import EmptyTable from '../EmptyTable'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'


// Компонент чипа для типа транзакции
const TransactionChip = ({ type }) => {
    const getChipStyle = (type) => {
        switch (type) {
            case 'lease':
                return {
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    color: '#FF9800',
                    border: '1px solid #FF9800'
                }
            case 'refund':
                return {
                    backgroundColor: 'rgba(0, 178, 74, 0.1)',
                    color: '#00B24A',
                    border: '1px solid #00B24A'
                }
            case 'deposit':
                return {
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    color: '#1976D2',
                    border: '1px solid #1976D2'
                }
            default:
                return {
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-muted)',
                    border: '1px solid var(--light-gray)'
                }
        }
    }

    return (
        <span style={{
            ...getChipStyle(type),
            padding: '6px 12px',
            borderRadius: '7px',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100px',
            height: '35px',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {type}
        </span>
    )
}


// Компонент DatePicker с кастомным календарем для мобильных
const MobileDatePicker = ({ label, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [tempDate, setTempDate] = useState(value)

    // Закрытие календаря при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.mobile-date-picker-container')) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const formatDisplayDate = (date) => {
        return date.format('MM/DD/YYYY')
    }

    const handleDateSelect = (selectedDate) => {
        setTempDate(selectedDate)
        onChange(selectedDate)
        setIsOpen(false)
    }

    const handleClear = () => {
        const clearedDate = dayjs()
        setTempDate(clearedDate)
        onChange(clearedDate)
        setIsOpen(false)
    }

    const handleToday = () => {
        const today = dayjs()
        setTempDate(today)
        onChange(today)
        setIsOpen(false)
    }

    const getCalendarDays = () => {
        const startOfMonth = tempDate.startOf('month')
        const endOfMonth = tempDate.endOf('month')
        const startOfCalendar = startOfMonth.startOf('week')
        const endOfCalendar = endOfMonth.endOf('week')

        const days = []
        let current = startOfCalendar

        while (current.isBefore(endOfCalendar, 'day') || current.isSame(endOfCalendar, 'day')) {
            days.push({
                date: current,
                isCurrentMonth: current.isSame(tempDate, 'month'),
                isSelected: current.isSame(value, 'day'),
                isToday: current.isSame(dayjs(), 'day')
            })
            current = current.add(1, 'day')
        }

        return days
    }

    const navigateMonth = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'month')
            : tempDate.add(1, 'month')
        setTempDate(newDate)
    }

    const navigateYear = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'year')
            : tempDate.add(1, 'year')
        setTempDate(newDate)
    }

    return (
        <div className="mobile-filter-group mobile-date-picker-container" style={{ position: 'relative' }}>
            <label className="mobile-filter-label">{label}</label>
            <div 
                className="mobile-filter-input"
                onClick={() => setIsOpen(!isOpen)}
                style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <span>{formatDisplayDate(value)}</span>
                <span style={{ color: 'var(--primary-orange)' }}>📅</span>
            </div>

            {isOpen && (
                <>
                    {/* Затемненный фон */}
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 9999
                    }} onClick={() => setIsOpen(false)} />
                    
                    {/* Календарь */}
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10000,
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-md)',
                        boxShadow: 'var(--shadow-card)',
                        width: '90vw',
                        maxWidth: '320px',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}>
                    {/* Заголовок календаря */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--spacing-sm)',
                        gap: '4px'
                    }}>
                        <button
                            onClick={() => navigateYear('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟪
                        </button>
                        <button
                            onClick={() => navigateMonth('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ‹
                        </button>
                        <span style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: '600',
                            color: 'var(--text-light)',
                            flex: 1,
                            textAlign: 'center'
                        }}>
                            {tempDate.format('MMMM YYYY')}
                        </span>
                        <button
                            onClick={() => navigateMonth('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ›
                        </button>
                        <button
                            onClick={() => navigateYear('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟫
                        </button>
                    </div>

                    {/* Дни недели */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                            <div key={day} style={{
                                padding: '6px 2px',
                                textAlign: 'center',
                                fontSize: 'var(--text-xs)',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Календарная сетка */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px'
                    }}>
                        {getCalendarDays().map((day, index) => (
                            <button
                                key={index}
                                onClick={() => handleDateSelect(day.date)}
                                style={{
                                    padding: '6px 2px',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-xs)',
                                    cursor: 'pointer',
                                    background: day.isSelected 
                                        ? 'var(--primary-orange)' 
                                        : day.isToday 
                                            ? 'var(--dark-gray)' 
                                            : 'transparent',
                                    color: day.isSelected 
                                        ? 'var(--white)' 
                                        : !day.isCurrentMonth 
                                            ? 'var(--text-muted)' 
                                            : day.isToday 
                                                ? 'var(--primary-orange)' 
                                                : 'var(--text-light)',
                                    fontWeight: day.isSelected || day.isToday ? '600' : '400',
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = 'var(--dark-gray)'
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = day.isToday ? 'var(--dark-gray)' : 'transparent'
                                    }
                                }}
                            >
                                {day.date.format('D')}
                            </button>
                        ))}
                    </div>

                    {/* Кнопки действий */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 'var(--spacing-sm)',
                        paddingTop: 'var(--spacing-sm)',
                        borderTop: '1px solid var(--light-gray)',
                        gap: 'var(--spacing-sm)'
                    }}>
                        <button
                            onClick={handleClear}
                            style={{
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.borderColor = 'var(--primary-orange)'
                                e.target.style.color = 'var(--primary-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.borderColor = 'var(--light-gray)'
                                e.target.style.color = 'var(--text-light)'
                            }}
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleToday}
                            style={{
                                background: 'var(--primary-orange)',
                                border: '1px solid var(--primary-orange)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--white)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--dark-orange)'
                                e.target.style.borderColor = 'var(--dark-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--primary-orange)'
                                e.target.style.borderColor = 'var(--primary-orange)'
                            }}
                        >
                            Today
                        </button>
                    </div>
                </div>
                </>
            )}
        </div>
    )
}

// Компонент пагинации для мобильных
const MobilePaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0)
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-xs)',
            flexWrap: 'wrap',
            justifyContent: 'center'
        }}>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handleFirstPage}
                disabled={safePage === 0}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handlePrevPage}
                disabled={safePage === 0}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-xs)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center'
            }}>
                {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, safeCount)} of {safeCount}
            </span>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardArrowRight style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />
            </button>
        </div>
    )
}

const MobilePaymentsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [paymentsList, setPaymentsList] = useState([])
    const [startDate, setStartDate] = useState(() => dayjs())
    const [endDate, setEndDate] = useState(() => dayjs().endOf('month'))
    const [showFilters, setShowFilters] = useState(false)


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - paymentsList?.txRecords?.length) : 0

    const handleChangePage = React.useCallback((event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }, [])

    const handleChangeRowsPerPage = React.useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }, [])

    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD 00:00:00')
            const dateEndFormated = endDate.format('YYYY-MM-DD 23:59:59')
            
            // Получаем токен один раз
            const token = await getAccessTokenSilently()
            
            const paymentsPesponse = await props.apiClient.setToken(token).getUserPayments(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setPaymentsList(paymentsPesponse)
          } catch (e) {
          }
      }
     
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleSetStartDate = React.useCallback((date) => {
      setStartDate(date)
    }, [])
  
    const handleSetEndDate = React.useCallback((date) => {
      setEndDate(date)
    }, [])

    return (
        <div style={{
            width: '100%',
            height: '100%',
            padding: 'var(--spacing-sm)',
            background: 'var(--black)',
            overflow: 'auto'
        }}>
            
            {/* Мобильные фильтры по дате */}
            <div className="mobile-filters">
                <div 
                    className="mobile-filters-header"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <span className="mobile-filters-title">Date Filters</span>
                    <span style={{ 
                        color: 'var(--text-muted)',
                        transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>
                
                {showFilters && (
                    <div className="mobile-filters-content">
                        <div className="mobile-filters-grid">
                            <MobileDatePicker 
                                label="From" 
                                value={startDate} 
                                onChange={handleSetStartDate}
                            />
                            <MobileDatePicker 
                                label="To" 
                                value={endDate} 
                                onChange={handleSetEndDate}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            {paymentsList?.txRecords?.length > 0 ? (
                <div>
                    {/* Мобильные карточки транзакций */}
                    <div style={{ padding: '0' }}>
                        {paymentsList?.txRecords?.map((row, index) => (
                            <div
                                key={index}
                                className="mobile-payment-card"
                            >
                                {/* Заголовок карточки */}
                                <div className="mobile-payment-header">
                                    <div>
                                        <div className="mobile-payment-amount" style={{ 
                                            color: row?.amtC < 0 ? '#eb5d0c' : '#00B24A',
                                        }}>
                                            {row?.amtC < 0 ? `-$${row?.amtC ? (Math.abs(row.amtC)/100).toFixed(2) : '0.00'}` : `+$${row?.amtC ? (row.amtC/100).toFixed(2) : '0.00'}`}
                                        </div>
                                        <div className="mobile-payment-date">
                                            {moment(row?.time).format('MMM DD, YYYY')}
                                        </div>
                                    </div>
                                    <TransactionChip type={row?.type} />
                                </div>

                                {/* Детали транзакции */}
                                <div className="mobile-payment-details">
                                    <div className="mobile-payment-detail">
                                        <div className="mobile-payment-detail-label">Description</div>
                                        <div className="mobile-payment-detail-value">
                                            {row?.memo?.replace(/(.{60})..+/, "$1…")}
                                        </div>
                                    </div>
                                    
                                    <div className="mobile-payment-detail">
                                        <div className="mobile-payment-detail-label">Balance</div>
                                        <div className="mobile-payment-detail-value" style={{ 
                                            color: '#1A53FF',
                                            fontWeight: '600'
                                        }}>
                                            ${row?.balC ? (row.balC/100).toFixed(2) : '0.00'}
                                        </div>
                                    </div>
                                </div>


                                {/* Время транзакции */}
                                <div className="mobile-payment-time">
                                    {moment(row?.time).format('h:mm A')}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Мобильная пагинация */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Строка с навигацией */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <MobilePaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={paymentsList?.total || 0}
                                onPageChange={handleChangePage}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">💰</div>
                    <h3 className="empty-state-title">No transactions found</h3>
                    <p className="empty-state-description">
                        You don't have any transaction records for the selected period.
                    </p>
                </div>
            )}
        </div>
    )
}

export default MobilePaymentsTable
